import { useState } from "react";
import { Header } from "@/components/Header";
import { StaticRouteBreadcrumbs } from "@/components/StaticRouteBreadcrumbs";
import { Footer } from "@/components/Footer";
import { AnnouncementBar } from "@/components/AnnouncementBar";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Mail, MessageCircle, Loader2, CheckCircle, Truck } from "lucide-react";
import { getSupabase } from "@/integrations/supabase/lazy";
import { toast } from "sonner";
import { trackLead } from "@/lib/analytics";

import { routeHead } from "@/lib/routeHead";

// Title/description come from scripts/routes.mjs, like the prerendered head.
const head = routeHead("/contact");

const Contact = () => {
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm(prev => ({ ...prev, [e.target.id]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.firstName || !form.email || !form.message) {
      toast.error("Please fill in all required fields");
      return;
    }
    setLoading(true);
    const submissionId = crypto.randomUUID();

    // Loaded here, on submit: imported at the top it made the whole page wait for the client.
    let supabase: Awaited<ReturnType<typeof getSupabase>>;
    try {
      supabase = await getSupabase();
      const { error } = await supabase.from("contact_submissions").insert({
        id: submissionId,
        first_name: form.firstName.trim(),
        last_name: form.lastName.trim(),
        email: form.email.trim(),
        subject: form.subject.trim() || "General Inquiry",
        message: form.message.trim(),
      });
      if (error) throw error;
    } catch {
      toast.error("Failed to send message. Please try again.");
      setLoading(false);
      return;
    }

    // Lead captured: show success immediately. Emails are best-effort.
    setSubmitted(true);
    trackLead("contact");
    setForm({ firstName: "", lastName: "", email: "", subject: "", message: "" });
    setLoading(false);

    // Send notification to owner + confirmation to visitor
    Promise.all([
      supabase.functions.invoke("send-transactional-email", {
        body: {
          templateName: "contact-notification",
          recipientEmail: "alondrilich@gmail.com",
          idempotencyKey: `contact-notify-${submissionId}`,
          templateData: {
            firstName: form.firstName.trim(),
            lastName: form.lastName.trim(),
            email: form.email.trim(),
            subject: form.subject.trim() || "General Inquiry",
            message: form.message.trim(),
          },
        },
      }).catch((err) => {
        console.error(`[Contact] notification email failed for submission ${submissionId}:`, err);
      }),
      supabase.functions.invoke("send-transactional-email", {
        body: {
          templateName: "contact-confirmation",
          recipientEmail: form.email.trim(),
          idempotencyKey: `contact-confirm-${submissionId}`,
          templateData: { firstName: form.firstName.trim() },
        },
      }).catch((err) => {
        console.error(`[Contact] confirmation email failed for submission ${submissionId}:`, err);
      }),
    ]);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>{head.title}</title>
        <meta name="description" content={head.description} />
        <link rel="canonical" href="https://loftloom.com/contact" />
        <meta property="og:title" content={head.title} />
        <meta property="og:description" content={head.description} />
        <meta property="og:url" content="https://loftloom.com/contact" />
        <meta property="og:type" content="website" />
      </Helmet>
      <AnnouncementBar />
      <Header />
      <StaticRouteBreadcrumbs />
      <main className="flex-1">
        <div className="max-w-5xl mx-auto px-4 py-16 md:py-24">
          <h1 className="page-title font-semibold mb-4 text-center">Contact LoftLoom</h1>
          <p className="text-muted-foreground text-center max-w-xl mx-auto mb-12">We're here to help. Reach out and we'll get back to you within 1 business day.</p>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="text-center p-6 rounded-2xl bg-sand/40">
              <Mail className="h-6 w-6 mx-auto mb-3 text-primary" />
              <h3 className="font-semibold text-foreground mb-1">Email</h3>
              <a href="mailto:hello@loftloom.com" className="text-sm text-muted-foreground hover:text-primary transition-colors">hello@loftloom.com</a>
            </div>
            <div className="text-center p-6 rounded-2xl bg-sand/40">
              <MessageCircle className="h-6 w-6 mx-auto mb-3 text-primary" />
              <h3 className="font-semibold text-foreground mb-1">AI Chat Assistant</h3>
              <p className="text-sm text-muted-foreground">Answers product questions 24/7 in the chat widget. For orders, email us.</p>
            </div>
            <div className="text-center p-6 rounded-2xl bg-sand/40">
              <Truck className="h-6 w-6 mx-auto mb-3 text-primary" />
              <h3 className="font-semibold text-foreground mb-1">Shipping & Returns</h3>
              <p className="text-sm text-muted-foreground">Free delivery over $120, 30-day returns.</p>
            </div>
          </div>

          <div className="max-w-2xl mx-auto">
            {submitted ? (
              <div className="text-center py-16 space-y-4">
                <CheckCircle className="h-12 w-12 text-primary mx-auto" />
                <h2 className="font-display text-2xl font-semibold text-foreground">Message Sent!</h2>
                <p className="text-muted-foreground">Thank you for reaching out. We'll get back to you within 1 business day.</p>
                <Button variant="outline" className="mt-4" onClick={() => setSubmitted(false)}>
                  Send Another Message
                </Button>
              </div>
            ) : (
              <>
                <h2 className="font-display text-xl font-semibold text-foreground mb-6">Send us a message</h2>
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">First Name *</Label>
                      <Input id="firstName" value={form.firstName} onChange={handleChange} placeholder="Jane" required />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">Last Name</Label>
                      <Input id="lastName" value={form.lastName} onChange={handleChange} placeholder="Doe" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input id="email" type="email" value={form.email} onChange={handleChange} placeholder="jane@example.com" required />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="subject">Subject</Label>
                    <Input id="subject" value={form.subject} onChange={handleChange} placeholder="Order inquiry, product question, etc." />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="message">Message *</Label>
                    <Textarea id="message" value={form.message} onChange={handleChange} placeholder="How can we help?" rows={5} required />
                  </div>
                  <Button type="submit" disabled={loading} className="w-full h-12 text-[12px] font-semibold uppercase tracking-[0.18em]">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    {loading ? "Sending..." : "Send Message"}
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Contact;
